import { faArrowRotateRight } from '@fortawesome/pro-light-svg-icons'
import { Fragment, useMemo } from 'react'
import { ButtonPrimary } from '../../../../../../../../../../../baseComponents/Button'
import PermissionChecker from '../../../../../../../../../../../baseComponents/PermissionChecker'
import { TwinIconToolTip } from '../../../../../../../../../../../baseComponents/TwinIcon'
import { TableData } from '../../../../../../../../../../../baseComponents/TwinTable/types'
import ModalSkipOperateStockOrder from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalSkipOperateStockOrder'
import { OrderData } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { SellCreateWorkSheetButton, SellDownloadButtonCSV, SellDownloadButtonPDF, SellDownloadPdfTemplateButton, SellDownloadTags, SellPdfPreview, SellPrepareOrderButton, SellQuickAccessButton, SellViewWorkSheetButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import useEditDeleteModal from '../../../../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getAllWorkSheetGroupStatuses, getUserPermissions } from '../../../../../../../../../../../utils/reducers/getters'
import { SellOrderDuplicateButton, SellOrderRecalculateButton } from '../../../SellOrderButtons'
import ModalProcessSellOrder from '../ModalProcessSellOrder'
import ModalReopenSellOrder from '../ModalReopenSellOrder'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import { OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import ModalOrderPaymentMethod from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalOrderPaymentMethod'

export interface SellOrderWaitingResponseStepButtonsProps extends OrderStepButtonsBaseComponentProps {
    onSubmit: (inputtable?: TableData) => Promise<void>
}

const SellOrderWaitingResponseStepButtons: React.FC<SellOrderWaitingResponseStepButtonsProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellOrderWaitingResponseStepButtonsLogic, ...rest }} className='mr-8' />
            <SellOrderProcessingButton title={t('processingSellOrder', 'Procesar pedido')} data={data} getCompleteData={onSubmit} />
        </div>
    )
}

const useSellOrderWaitingResponseStepButtonsLogic = ({ data, onSubmit, isReadOnly, setCompleteData }: OrderStepButtonsBaseComponentProps) => {
    const {t} = useTwinTranslation()
    const name = 'sell_order_waiting_response_step_buttons'
    const workSheets = getAllWorkSheetGroupStatuses()
    const Order = data?.header
    const OrderId = Order?.id
    const hideSellQuickAccesButton = !Order?.QuoteOrderId && !Order?.SellOrderId && !Order?.SellDeliveryNoteId && !Order?.SellInvoiceId && !Order?.WorkSheetId && !Order?.relatedSellOrders?.length && !Order?.relatedSellDeliveryNotes?.length && !Order?.relatedSellInvoices?.length && !Order?.relatedWorkSheets?.length

    const defaultButtons = [{ "id": "previewSellOrder" }, { "id": "downloadSellOrder" }, { "id": "quickAccess" }, { "id": "duplicateSell" }, { "id": "sellPrepareOrder" }, { "id": "createWorkSheet" }, { "id": "viewWorkSheet" }]

    const sellOrderDuplicateOrderText = t('duplicateOrder', 'Duplicar factura')
    const sellOrderReopenOrderText = t('ReopenSellOrder', 'Reabrir pedido')
    const sellOrderPrepareOrderText = t('prepareOrder', 'Preparar pedido')
    const sellOrderRecalculateOrderText = t('recalculateSellOrder', 'Recalcular pedido')
    const sellOrderDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellOrderPrintTagsText = t('printTags', 'Imprimir etiquetas')
    const sellViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellCreateWorkSheetText = t('createWorkSheet', 'Crear hoja de trabajo')
    const sellOrderQuickAccessText = t('quickAccess', 'Acceso rápido')
    const sellOrderPreviewPDFText = t('previewPdf', 'Previsualizar PDF')
    const sellOrderDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const sellOrderDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const sellOrderDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellOrderVariablesText = t('variables', 'Variables')
    const sellOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateSell: {
                component: SellOrderDuplicateButton,
                extraProps: {
                    title: sellOrderDuplicateOrderText,
                    id: 'duplicateSell',
                    allRowData: data,
                }
            },
            reopenSellOrder: {
                component: SellOrderReopenButton,
                extraProps: {
                    title: sellOrderReopenOrderText,
                    id: 'reopenSellOrder',
                    OrderId,
                    onSubmit
                }
            },
            sellPrepareOrder: {
                component: SellPrepareOrderButton,
                extraProps: {
                    title: sellOrderPrepareOrderText,
                    id: 'sellPrepareOrder',
                    Order: data,
                    setCompleteData
                }
            },
            recalculateSellOrder: {
                component: SellOrderRecalculateButton,
                extraProps: {
                    title: sellOrderRecalculateOrderText,
                    id: 'recalculateSellOrder',
                    allRowData: data,
                    onSubmit,
                }
            },
            downloadSellCsv: {
                component: SellDownloadButtonCSV,
                extraProps: {
                    title: sellOrderDownloadCSVText,
                    id: 'downloadSellCsv',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            downloadSellTag: {
                component: SellDownloadTags,
                extraProps: {
                    title: sellOrderPrintTagsText,
                    id: 'downloadSellTag',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            viewWorkSheet: {
                component: SellViewWorkSheetButton,
                showButton: Order?.WorkSheetId,
                extraProps: {
                    title: sellViewWorkSheetText,
                    id: 'viewWorkSheet',
                    WorkSheetId: Order?.WorkSheetId
                }
            },
            createWorkSheet: {
                component: SellCreateWorkSheetButton,
                showButton: !Order?.WorkSheetId && !!workSheets?.WorkSheetActiveStatusGroups?.length,
                extraProps: {
                    title: sellCreateWorkSheetText,
                    id: 'createWorkSheet',
                    Order,
                    createURL: '/api/app/order/sell/workSheet/createWorkSheetBySellOrder',
                    orderField: 'SellOrderId',
                    completeData: data
                }
            },
            quickAccess: {
                component: SellQuickAccessButton,
                showButton: !hideSellQuickAccesButton,
                extraProps: {
                    title: sellOrderQuickAccessText,
                    id: 'quickAccess',
                    Order,
                }
            },
            previewSellOrder: {
                component: SellPdfPreview,
                extraProps: {
                    title: sellOrderPreviewPDFText,
                    id: 'previewSellOrder',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            downloadSellOrderTemplate: {
                component: SellDownloadPdfTemplateButton,
                extraProps: {
                    title: sellOrderDownloadPDFTemplateText,
                    id: 'downloadSellOrderTemplate',
                    Order,
                    typeTemplate: 'sellOrder',
                    key: 'downloadSellOrderPDFTemplate'
                }
            },
            downloadSellOrder: {
                component: SellDownloadButtonPDF,
                extraProps: {
                    title: sellOrderDownloadPDFText,
                    id: 'downloadSellOrder',
                    Order,
                    typeTemplate: 'sellOrder',
                    key: 'downloadSellOrder'
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: sellOrderDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    isReadOnly,
                    onSubmit,
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: sellOrderVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
            sellProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: sellOrderProductTypesText,
                    id: 'sellProductTypes',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
        }
    }, [Order, OrderId, data, hideSellQuickAccesButton, isReadOnly, onSubmit, sellCreateWorkSheetText, sellOrderDivideIntoChaptersText, sellOrderDownloadCSVText, sellOrderDownloadPDFTemplateText, sellOrderDownloadPDFText, sellOrderDuplicateOrderText, sellOrderPreviewPDFText, sellOrderPrintTagsText, sellOrderQuickAccessText, sellOrderRecalculateOrderText, sellOrderReopenOrderText, sellOrderVariablesText, sellViewWorkSheetText, workSheets?.WorkSheetActiveStatusGroups?.length, sellOrderPrepareOrderText, setCompleteData, sellOrderProductTypesText])
    
    return { name, ButtonComponents, defaultButtons }
}

export const SellOrderWaitingResponseStepButtonsNew: React.FC<SellOrderWaitingResponseStepButtonsProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellOrderWaitingResponseStepButtonsLogic, ...rest }} className='mr-8' />
            <SellOrderProcessingButton title={t('processingSellOrder', 'Procesar pedido')} data={data} getCompleteData={onSubmit} />
        </div>
    )
}

interface SellOrderProcessingButtonProps {
    title: string
    children?: React.ReactNode
    data: OrderData
    getCompleteData: (inputtable?: TableData) => Promise<void>
}

const SellOrderProcessingButton: React.FC<SellOrderProcessingButtonProps> = ({ title,  data, getCompleteData }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return (
        <Fragment>
            <ButtonPrimary id={'sellOrder' + data?.header?.id} onClick={() => setOpenModal({ type: 'create', allRowData: { data }})}>{title}</ButtonPrimary>
            {openModal?.type === 'create' ? <ModalProcessSellOrder setOpenModal={setOpenModal} data={data} getCompleteData={getCompleteData} /> : null}
            {openModal?.type === 'edit' ? <ModalSkipOperateStockOrder checkStockURLs={'/api/app/order/sell/sellOrder/checkAllSellOrderProductsStock'} setOpenModal={setOpenModal} allRowData={openModal.allRowData} finalizeOrder={openModal.allRowData?.finalizeOrder} /> : null}
            {openModal?.type === 'delete' ? <ModalOrderPaymentMethod headerData={data.header} setOpened={() => setOpenModal(null)} allRowData={openModal.allRowData} totalsOrderTableData={openModal.allRowData?.totalsOrderTableData} beforeSubmit={openModal.allRowData.finalizeOrder} onSubmit={openModal.allRowData.onSubmit} /> : null}
        </Fragment>
    )
}

interface SellOrderReopenButtonProps {
    onSubmit: () => void
    id: string
    title: string
    OrderId: number
    children?: React.ReactNode
}

export const SellOrderReopenButton: React.FC<SellOrderReopenButtonProps> = ({ OrderId, onSubmit, id, title, children }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return (
        <PermissionChecker permission='update' userPermissions={getUserPermissions('sell.order')}>
            <div onClick={() => setOpenModal({ allRowData: { id: OrderId }, type: 'edit' })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-green-43 regular22' message={title} icon={faArrowRotateRight} id={id} /> }
            </div>
            {openModal?.type === 'edit' ? <ModalReopenSellOrder setOpened={() => setOpenModal(null)} onSubmit={onSubmit} allRowData={openModal.allRowData} /> : null}
        </PermissionChecker>
    )
}

export default SellOrderWaitingResponseStepButtons